import { useState } from 'react';
import { Lock } from 'lucide-react';
import client from '../../api/client';
import { useFetch } from '../../hooks/useFetch';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';

const VIDE = { nom: '', prenom: '', email: '', password: '', role_id: '' };
const ROLE_RACINE = 'super_administrateur';

export function UsersManager() {
  const { user: moi } = useAuth();
  const monId = moi?.sub != null ? Number(moi.sub) : null;

  const { data: users, loading, refetch } = useFetch(() => client.get('/admin/users'), []);
  const { data: roles } = useFetch(() => client.get('/admin/roles'), []);
  const [creation, setCreation] = useState(null);
  const [edition, setEdition] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      await client.post('/admin/users', creation);
      setCreation(null);
      refetch();
    } catch (err) {
      setErrors(err.errors || { _global: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  function ouvrirEdition(u) {
    setEdition({ id: u.id, nom: u.nom, prenom: u.prenom, email: u.email, password: '' });
    setErrors({});
  }

  async function handleEditSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    try {
      const { id, ...payload } = edition;
      if (!payload.password) delete payload.password;
      await client.put(`/admin/users/${id}`, payload);
      setEdition(null);
      refetch();
    } catch (err) {
      setErrors(err.errors || { _global: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActif(user) {
    await client.put(`/admin/users/${user.id}/statut`, { actif: !user.actif });
    refetch();
  }

  async function changerRole(userId, roleId) {
    await client.put(`/admin/users/${userId}/role`, { role_id: roleId });
    refetch();
  }

  async function supprimer(user) {
    if (!window.confirm(`Supprimer le compte de ${user.prenom} ${user.nom} ?`)) return;
    await client.delete(`/admin/users/${user.id}`);
    refetch();
  }

  if (loading) return <p>Chargement...</p>;

  return (
    <section>
      <div className="admin-toolbar">
        <h1>Utilisateurs</h1>
        {!creation && !edition && <Button onClick={() => setCreation({ ...VIDE })}>+ Nouvel utilisateur</Button>}
      </div>

      {creation && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="admin-form__field">
            <label>Prénom</label>
            <input value={creation.prenom} onChange={(e) => setCreation({ ...creation, prenom: e.target.value })} required />
            {errors.prenom && <span className="admin-form__error">{errors.prenom}</span>}
          </div>

          <div className="admin-form__field">
            <label>Nom</label>
            <input value={creation.nom} onChange={(e) => setCreation({ ...creation, nom: e.target.value })} required />
            {errors.nom && <span className="admin-form__error">{errors.nom}</span>}
          </div>

          <div className="admin-form__field">
            <label>Email</label>
            <input
              type="email"
              value={creation.email}
              onChange={(e) => setCreation({ ...creation, email: e.target.value })}
              required
            />
            {errors.email && <span className="admin-form__error">{errors.email}</span>}
          </div>

          <div className="admin-form__field">
            <label>Mot de passe (min. 8 caractères)</label>
            <input
              type="password"
              value={creation.password}
              onChange={(e) => setCreation({ ...creation, password: e.target.value })}
              required
            />
            {errors.password && <span className="admin-form__error">{errors.password}</span>}
          </div>

          <div className="admin-form__field">
            <label>Rôle</label>
            <select value={creation.role_id} onChange={(e) => setCreation({ ...creation, role_id: e.target.value })} required>
              <option value="">Sélectionner...</option>
              {roles?.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.nom}
                </option>
              ))}
            </select>
            {errors.role_id && <span className="admin-form__error">{errors.role_id}</span>}
          </div>

          {errors._global && <p className="admin-form__error">{errors._global}</p>}

          <div className="admin-form__actions">
            <Button type="submit" disabled={submitting}>
              Créer
            </Button>
            <Button type="button" variant="secondary" onClick={() => setCreation(null)}>
              Annuler
            </Button>
          </div>
        </form>
      )}

      {edition && (
        <form className="admin-form" onSubmit={handleEditSubmit}>
          <div className="admin-form__field">
            <label>Prénom</label>
            <input value={edition.prenom} onChange={(e) => setEdition({ ...edition, prenom: e.target.value })} required />
            {errors.prenom && <span className="admin-form__error">{errors.prenom}</span>}
          </div>

          <div className="admin-form__field">
            <label>Nom</label>
            <input value={edition.nom} onChange={(e) => setEdition({ ...edition, nom: e.target.value })} required />
            {errors.nom && <span className="admin-form__error">{errors.nom}</span>}
          </div>

          <div className="admin-form__field">
            <label>Email</label>
            <input
              type="email"
              value={edition.email}
              onChange={(e) => setEdition({ ...edition, email: e.target.value })}
              required
            />
            {errors.email && <span className="admin-form__error">{errors.email}</span>}
          </div>

          <div className="admin-form__field">
            <label>Nouveau mot de passe (laisser vide pour ne pas changer)</label>
            <input
              type="password"
              value={edition.password}
              onChange={(e) => setEdition({ ...edition, password: e.target.value })}
            />
            {errors.password && <span className="admin-form__error">{errors.password}</span>}
          </div>

          {errors._global && <p className="admin-form__error">{errors._global}</p>}

          <div className="admin-form__actions">
            <Button type="submit" disabled={submitting}>
              Enregistrer
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEdition(null)}>
              Annuler
            </Button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Email</th>
            <th>Rôle</th>
            <th>Actif</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {users?.map((u) => {
            const estRacine = u.role_nom === ROLE_RACINE;
            const estMoi = monId != null && Number(u.id) === monId;

            return (
              <tr key={u.id}>
                <td>
                  {u.prenom} {u.nom}
                  {estRacine && (
                    <span className="badge-racine" title="Compte racine protégé">
                      <Lock size={12} /> Super administrateur
                    </span>
                  )}
                </td>
                <td>{u.email}</td>
                <td>
                  {estRacine ? (
                    <span>{u.role_nom}</span>
                  ) : (
                    <select value={u.role_id} onChange={(e) => changerRole(u.id, e.target.value)}>
                      {roles?.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.nom}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>{u.actif ? 'Oui' : 'Non'}</td>
                <td className="admin-table__actions">
                  {(!estRacine || estMoi) && <button onClick={() => ouvrirEdition(u)}>Modifier</button>}
                  {!estRacine && (
                    <>
                      <button onClick={() => toggleActif(u)}>{u.actif ? 'Désactiver' : 'Activer'}</button>
                      <button onClick={() => supprimer(u)}>Supprimer</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
